import { useEffect, useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { Input } from '@/components/ui/Input/input';
import { useUrlSearch } from '@/hooks/useUrlSearch';
import { cn } from '@/utils/cn';

type GamesSearchBarProps = {
  placeholder: string;
  searchKey?: string;
  className?: string;
  delay?: number;
  resetPageOnSearch?: boolean;
  onSearchChange?: (value: string) => void;
  onSearchOpenChange?: (isOpen: boolean) => void;
};

export function GamesSearchBar({
  delay,
  placeholder,
  className,
  searchKey = 'search',
  resetPageOnSearch,
  onSearchChange,
  onSearchOpenChange,
}: GamesSearchBarProps) {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const { searchValue, setSearchValue, searchParam } = useUrlSearch({
    delay,
    searchKey,
    resetPageOnSearch,
  });
  const isSearchOpen = isSearchFocused || Boolean(searchValue);

  useEffect(() => {
    onSearchChange?.(searchParam);
  }, [onSearchChange, searchParam]);

  useEffect(() => {
    onSearchOpenChange?.(isSearchOpen);
  }, [isSearchOpen, onSearchOpenChange]);

  const handleClear = () => {
    setSearchValue('');
    onSearchChange?.('');
    setIsSearchFocused(false);
  };

  return (
    <div className={cn('absolute right-0 w-full flex justify-end', className)}>
      <Input
        placeholder={placeholder}
        value={searchValue}
        onChange={event => setSearchValue(event.target.value)}
        containerClassName={cn('transition-all duration-300 size-11 md:w-[15.625rem] bg-c900', isSearchOpen && 'bg-transparent w-full')}
        className={cn(
          'pr-0 transition-all duration-300 ml-auto h-11 w-full lg:pl-12',
          isSearchOpen ? 'pl-12' : 'max-md:placeholder:opacity-0',
        )}
        icon={<Icon name="search" className="text-c300 size-6" />}
        iconWrapperClassName={cn('transition-[left] duration-300', !isSearchOpen && 'max-md:left-2.5')}
        onFocus={() => setIsSearchFocused(true)}
        onBlur={() => setIsSearchFocused(false)}
      />

      {searchValue && (
        <button
          onClick={handleClear}
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-violet-c400"
          aria-label="Clear search"
        >
          <Icon name="x_close" className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
